import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { theme as defaultTheme } from './theme';

export const ThemeContext = React.createContext(defaultTheme);

const registerCSSVariables = (theme) => {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;
  const entries = {
    '--color-bg': theme.colors.background,
    '--color-surface': theme.colors.surface,
    '--color-surface-elevated': theme.colors.surfaceElevated,
    '--color-surface-muted': theme.colors.surfaceMuted,
    '--color-surface-transparent': theme.colors.surfaceTransparent,
    '--color-border': theme.colors.border,
    '--color-border-muted': theme.colors.borderMuted,
    '--color-accent': theme.colors.accent,
    '--color-accent-strong': theme.colors.accentStrong,
    '--color-accent-soft': theme.colors.accentSoft,
    '--color-text-primary': theme.colors.textPrimary,
    '--color-text-secondary': theme.colors.textSecondary,
    '--color-text-tertiary': theme.colors.textMuted,
    '--color-text-inverse': theme.colors.textInverse,
    '--color-success': theme.colors.success,
    '--color-warning': theme.colors.warning,
    '--color-danger': theme.colors.danger,
    '--shadow-soft': theme.shadows.soft,
    '--shadow-ring': theme.shadows.ring,
    '--shadow-border': theme.shadows.border,
    '--radius-sm': `${theme.radii.sm}px`,
    '--radius-md': `${theme.radii.md}px`,
    '--radius-lg': `${theme.radii.lg}px`,
    '--radius-xl': `${theme.radii.xl}px`,
    '--radius-pill': `${theme.radii.pill}px`,
    '--transition-default': '220ms ease',
    '--transition-fast': '120ms ease'
  };

  Object.entries(entries).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
};

export const ThemeProvider = ({ theme = defaultTheme, children }) => {
  const memoTheme = useMemo(() => ({ ...defaultTheme, ...theme }), [theme]);

  useEffect(() => {
    registerCSSVariables(memoTheme);
  }, [memoTheme]);

  return <ThemeContext.Provider value={memoTheme}>{children}</ThemeContext.Provider>;
};

ThemeProvider.propTypes = {
  theme: PropTypes.object,
  children: PropTypes.node.isRequired
};

export const useTheme = () => React.useContext(ThemeContext);

export default ThemeProvider;
